import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * PublicInterest — the public, no-login landing page a recipient reaches after
 * scanning the QR / opening the short URL on their postcard (Close the Loop
 * CL-2 / story A2).
 *
 * Privacy (NFR-1): this page renders ONLY the club's already-public branding
 * (logo, name, contact) + a generic greeting. It NEVER shows the recipient's
 * name, call sign, or address — the opaque {code} in the URL maps to those
 * server-side only and is never echoed. Unknown/blank codes render the same
 * generic friendly page (NFR-3), so nothing here confirms a code is "real".
 *
 * Mobile-first + accessible (NFR-10): recipients scan with a phone, so the
 * layout is single-column, large-tap-target, keyboard-navigable, and high
 * contrast. Lives OUTSIDE the authed app shell (routed in App.js).
 */
export default function PublicInterest() {
  const { code } = useParams();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    // The scan itself was already recorded by the GET /r/{code} resolver that
    // redirected here; this is a pure read of PII-free club branding.
    fetch(`/api/public/postcard-interest/${encodeURIComponent(code || "")}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active) setClub(data);
      })
      .catch(() => {
        // Network hiccup — fall back to the generic greeting below.
        if (active) setClub(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [code]);

  const clubName = club?.club_name || "A local ham radio club";
  const message =
    club?.message || "Your local ham radio club would love to have you on the air!";
  const location = [club?.city, club?.state].filter(Boolean).join(", ");

  return (
    <div style={styles.page}>
      <main style={styles.card} aria-busy={loading}>
        {club?.logo_url ? (
          <img
            src={club.logo_url}
            alt={`${clubName} logo`}
            style={styles.logo}
          />
        ) : (
          <div style={styles.wordmark} aria-hidden="true">
            📻
          </div>
        )}

        <h1 style={styles.clubName}>{clubName}</h1>
        {location && <p style={styles.location}>{location}</p>}

        <p style={styles.message}>{message}</p>

        <div style={styles.cta}>
          {club?.contact_email ? (
            <a
              href={`mailto:${club.contact_email}?subject=${encodeURIComponent(
                "I'm interested in the club!"
              )}`}
              style={styles.button}
            >
              Get in touch
            </a>
          ) : (
            <p style={styles.muted}>
              Ask a local operator how to get involved — we'd love to hear from you.
            </p>
          )}
        </div>

        <p style={styles.footer}>
          You received a postcard from your local amateur radio club. Welcome to
          the hobby!
        </p>
      </main>
    </div>
  );
}

// Self-contained inline styles so this public page carries no dependency on the
// authed app's stylesheet/shell. Dark-blue palette matches the app brand.
const styles = {
  page: {
    minHeight: "100vh",
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    background: "linear-gradient(160deg, #0d1b2a 0%, #1f3a5f 100%)",
    fontFamily:
      "'Helvetica Neue', Helvetica, Arial, sans-serif",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "#ffffff",
    borderRadius: 16,
    padding: "32px 24px",
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
    boxSizing: "border-box",
  },
  logo: {
    maxWidth: "70%",
    maxHeight: 120,
    objectFit: "contain",
    margin: "0 auto 8px",
  },
  wordmark: { fontSize: 56, lineHeight: 1, marginBottom: 8 },
  clubName: {
    fontSize: 24,
    fontWeight: 800,
    color: "#0d1b2a",
    margin: "12px 0 4px",
  },
  location: { fontSize: 14, color: "#64748b", margin: "0 0 16px" },
  message: {
    fontSize: 18,
    lineHeight: 1.5,
    color: "#1f3a5f",
    margin: "16px 0 24px",
  },
  cta: { margin: "8px 0 20px" },
  button: {
    display: "inline-block",
    background: "#1f3a5f",
    color: "#ffffff",
    textDecoration: "none",
    fontSize: 17,
    fontWeight: 700,
    padding: "14px 28px",
    borderRadius: 10,
    minWidth: 200,
  },
  muted: { fontSize: 15, color: "#475569", lineHeight: 1.5 },
  footer: {
    fontSize: 12,
    color: "#94a3b8",
    marginTop: 16,
    lineHeight: 1.5,
  },
};
